import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Stack,
  TextField,
  IconButton,
  Tooltip,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { getAlarmPhones, setPhoneNo } from "../../api/webUserAPI";

const EMPTY = ["", "", ""];

// Global phone validation (international): optional leading "+", then 7-15
// digits (E.164-style). Spaces, dashes, dots and parentheses are allowed as
// separators and ignored.
function isValidPhone(value) {
  const v = String(value ?? "").trim();
  if (!v) return false;
  const cleaned = v.replace(/[\s().-]/g, "");
  return /^\+?[1-9]\d{6,14}$/.test(cleaned);
}

// Normalize whatever GetAlarmPhones returns into [p1, p2, p3].
function normalizePhones(data) {
  if (Array.isArray(data)) {
    return [data[0] ?? "", data[1] ?? "", data[2] ?? ""].map((v) =>
      v == null ? "" : String(v)
    );
  }
  if (data && typeof data === "object") {
    const pick = (...keys) => {
      for (const k of keys) {
        if (data[k] != null) return String(data[k]);
      }
      return "";
    };
    return [
      pick("phoneNo1", "PhoneNo1", "phone1", "phoneNumber1"),
      pick("phoneNo2", "PhoneNo2", "phone2", "phoneNumber2"),
      pick("phoneNo3", "PhoneNo3", "phone3", "phoneNumber3"),
    ];
  }
  return [...EMPTY];
}

export default function AlarmPhones() {
  const { t } = useTranslation();
  const [phones, setPhones] = useState([...EMPTY]);
  const [drafts, setDrafts] = useState([...EMPTY]);
  const [editing, setEditing] = useState([false, false, false]);
  const [savingIdx, setSavingIdx] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const loadPhones = useCallback(async () => {
    try {
      const data = await getAlarmPhones();
      setPhones(normalizePhones(data));
    } catch (e) {
      console.error("getAlarmPhones failed:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPhones();
  }, [loadPhones]);

  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const startEdit = (i) => {
    setDrafts((d) => {
      const next = [...d];
      next[i] = phones[i];
      return next;
    });
    setEditing((e) => {
      const next = [...e];
      next[i] = true;
      return next;
    });
  };

  const cancelEdit = (i) => {
    setEditing((e) => {
      const next = [...e];
      next[i] = false;
      return next;
    });
  };

  const setDraft = (i, val) =>
    setDrafts((d) => {
      const next = [...d];
      next[i] = val;
      return next;
    });

  // Persist a value (save = draft, delete = "") for phone index i (0-based).
  const persist = async (i, value, successKey, fallbackMsg) => {
    if (savingIdx !== -1) return;
    try {
      setSavingIdx(i);
      await setPhoneNo(i + 1, value);
      setPhones((p) => {
        const next = [...p];
        next[i] = value ?? "";
        return next;
      });
      cancelEdit(i);
      setSnackbar({
        open: true,
        message: t(successKey, fallbackMsg),
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error?.message ||
          t("adminConfig.alarmPhones.error", "Operation failed"),
        severity: "error",
      });
    } finally {
      setSavingIdx(-1);
    }
  };

  const handleSave = (i) => {
    const value = (drafts[i] || "").trim();
    if (!isValidPhone(value)) {
      setSnackbar({
        open: true,
        message: t(
          "adminConfig.alarmPhones.invalid",
          "Please enter a valid phone number"
        ),
        severity: "error",
      });
      return;
    }
    persist(i, value, "adminConfig.alarmPhones.saved", "Number saved");
  };

  // Delete clears the number by sending null to the backend.
  const handleDelete = (i) =>
    persist(i, null, "adminConfig.alarmPhones.deleted", "Number deleted");

  return (
    <Box sx={{ maxWidth: { sm: "100%", md: "1700px" }, mb: 2 }}>
      <Card sx={{ borderWidth: 2, borderStyle: "solid" }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
            {t("adminConfig.alarmPhones.title", "Phone Numbers")}
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", md: "flex-start" }}
            >
              {[0, 1, 2].map((i) => {
                const isEditing = editing[i];
                const isSaving = savingIdx === i;
                const draftVal = drafts[i] || "";
                const invalid =
                  isEditing &&
                  draftVal.trim() !== "" &&
                  !isValidPhone(draftVal);
                return (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 0.5,
                      flex: 1,
                    }}
                  >
                    <TextField
                      label={`${t(
                        "adminConfig.alarmPhones.phone",
                        "Phone"
                      )} ${i + 1}`}
                      value={isEditing ? drafts[i] : phones[i]}
                      onChange={(e) => setDraft(i, e.target.value)}
                      InputProps={{ readOnly: !isEditing || isSaving }}
                      error={invalid}
                      helperText={
                        invalid
                          ? t(
                              "adminConfig.alarmPhones.invalid",
                              "Please enter a valid phone number"
                            )
                          : " "
                      }
                      size="small"
                      fullWidth
                      type="tel"
                    />
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        height: 40,
                        flexShrink: 0,
                      }}
                    >
                    {isEditing ? (
                      <>
                        <Tooltip title={t("common.save", "Save")}>
                          <span>
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleSave(i)}
                              disabled={isSaving || !isValidPhone(draftVal)}
                            >
                              {isSaving ? (
                                <CircularProgress size={18} />
                              ) : (
                                <CheckIcon />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t("common.cancel", "Cancel")}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => cancelEdit(i)}
                              disabled={isSaving}
                            >
                              <CloseIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Tooltip title={t("common.edit", "Edit")}>
                          <span>
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => startEdit(i)}
                              disabled={savingIdx !== -1}
                            >
                              <EditIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t("common.delete", "Delete")}>
                          <span>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleDelete(i)}
                              disabled={savingIdx !== -1 || !phones[i]}
                            >
                              {isSaving ? (
                                <CircularProgress size={18} />
                              ) : (
                                <DeleteIcon />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
